import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Image as ImageIcon, X } from "lucide-react";

const STATUSES = ["Open","Under Assessment","Quote Requested","Awaiting Approval","Scheduled","In Progress","Completed","Awaiting Reinspection","Closed","Deferred","Not Proceeding"];
const RISK = ["Low","Medium","High","Critical"];

export default function Defects() {
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [status, setStatus] = useState("all");
  const [risk, setRisk] = useState(params.get("risk") || "all");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ description:"", risk_level:"Medium", asset_id:"" });
  const [files, setFiles] = useState([]);

  const load = useCallback(async () => {
    const p = {};
    if (status!=="all") p.status = status;
    if (risk!=="all") p.risk = risk;
    const { data } = await api.get("/defects", { params: p });
    setRows(data);
  }, [status, risk]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/assets").then((r)=>setAssets(r.data)); }, []);

  const save = async () => {
    try {
      const { data } = await api.post("/defects", f);
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach((file) => fd.append("files", file));
        try { await api.post(`/defects/${data.id}/photos`, fd); }
        catch { toast.error("Photos failed to upload"); }
      }
      toast.success("Defect logged"); setOpen(false);
      setFiles([]); setF({description:"",risk_level:"Medium",asset_id:""});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStatusFor = async (id, s) => {
    try { await api.put(`/defects/${id}`, { status: s, ...(s==="Closed"?{closure_date:new Date().toISOString()}:{}) }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="defects-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Register</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Defects</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-defect-btn"><Plus className="w-4 h-4 mr-1"/>New defect</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Raise defect</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Asset (optional)</Label>
                <Select value={f.asset_id} onValueChange={(v)=>setF({...f, asset_id:v})}>
                  <SelectTrigger><SelectValue placeholder="Select asset"/></SelectTrigger>
                  <SelectContent className="max-h-72">{assets.map((a)=><SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Description *</Label><Textarea value={f.description} onChange={(e)=>setF({...f, description:e.target.value})} data-testid="new-defect-desc"/></div>
              <div><Label>Risk level</Label>
                <Select value={f.risk_level} onValueChange={(v)=>setF({...f, risk_level:v})}>
                  <SelectTrigger data-testid="new-defect-risk"><SelectValue/></SelectTrigger>
                  <SelectContent>{RISK.map((r)=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Photos (optional)</Label>
                <input type="file" multiple accept="image/*" capture="environment"
                  onChange={(e)=>setFiles(Array.from(e.target.files||[]))}
                  className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-slate-200 file:bg-slate-50 file:text-slate-700 hover:file:bg-slate-100"
                  data-testid="defect-photos"/>
                {files.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {files.map((file, i)=>(
                      <div key={`${file.name}-${file.size}-${i}`} className="text-xs bg-slate-100 border border-slate-200 rounded px-2 py-1 flex items-center gap-1">
                        <ImageIcon className="w-3 h-3"/><span className="truncate max-w-[10rem]">{file.name}</span>
                        <button type="button" className="text-slate-400 hover:text-slate-700" onClick={()=>setFiles(files.filter((_,j)=>j!==i))}><X className="w-3 h-3"/></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter><Button onClick={save} disabled={!f.description} data-testid="new-defect-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56" data-testid="defect-filter-status"><SelectValue placeholder="Status"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s)=><SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={risk} onValueChange={setRisk}>
          <SelectTrigger className="w-40" data-testid="defect-filter-risk"><SelectValue placeholder="Risk"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All risk</SelectItem>
            {RISK.map((r)=><SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200"><CardContent className="p-4">
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <Table className="dense-table">
            <TableHeader><TableRow>
              <TableHead>Description</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead>Risk</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Identified</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((d)=>{
                const a = assets.find((x)=>x.id===d.asset_id);
                return (
                  <TableRow key={d.id} data-testid={`defect-row-${d.id}`}>
                    <TableCell><div className="text-sm text-slate-900 max-w-md">{d.description}</div></TableCell>
                    <TableCell className="text-xs text-slate-600">{a?.name||"—"}</TableCell>
                    <TableCell><Badge className={d.risk_level==="Critical"||d.risk_level==="High"?"bg-red-100 text-red-800 border-0":"bg-amber-100 text-amber-800 border-0"}>{d.risk_level}</Badge></TableCell>
                    <TableCell>
                      <Select value={d.status} onValueChange={(v)=>setStatusFor(d.id, v)}>
                        <SelectTrigger className="h-8 w-44 text-xs" data-testid={`defect-status-${d.id}`}><SelectValue/></SelectTrigger>
                        <SelectContent>{STATUSES.map((s)=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(d.date_identified).toLocaleDateString()} · {d.identified_by}</TableCell>
                    <TableCell className="text-xs"><span className="text-slate-500">{d.closure_date ? `Closed ${new Date(d.closure_date).toLocaleDateString()}` : "—"}</span></TableCell>
                  </TableRow>
                );
              })}
              {rows.length===0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No defects match.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>
    </div>
  );
}
